// Per-tab login sessions (backend: services/tab_session_interface.py,
// services/tab_sessions.py, blueprints/session_tabs.py).
//
//   - every tab has its OWN server session, so tabs log in and out separately
//   - a refresh keeps the tab logged in
//   - a brand-new tab must log in (it has no session yet)
//   - a duplicated tab stays logged in, but on its own copy of the session,
//     so logging out of one copy leaves the other logged in
//   - closing the tab or the browser logs that tab out within a few seconds
//   - 15 minutes without activity in a tab logs that tab out
//
// The tab's server session ID lives in sessionStorage - kept on refresh, empty
// in a new tab, copied into a duplicated tab, gone when the tab closes - and is
// sent with every API request in the X-Tab-Session header (hooks below).

import axios from 'axios';

const SERVER_URL = process.env.REACT_APP_API_URL || '';

const SESSION_HEADER = 'X-Tab-Session';
const SESSION_ENDED_HEADER = 'X-Tab-Session-Ended';

const SESSION_ID_KEY = 'dmvnav_tab_session';
const LOGGED_IN_KEY = 'isLoggedIn';
const LAST_ACTIVITY_KEY = 'dmvnav_last_activity';

export const IDLE_LIMIT_MS = 15 * 60 * 1000;
// Heartbeat at most this often while the user is active. Keeps the server's
// idle timer (16 min) within this much of the tab's (15 min).
const HEARTBEAT_INTERVAL_MS = 30 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;
const IDLE_CHECK_INTERVAL_MS = 15 * 1000;
// How long a loading tab waits to hear whether another open tab has the same
// session ID - i.e. whether it is a duplicate.
const DUPLICATE_CHECK_TIMEOUT_MS = 200;

const channel = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('dmvnav-session')
  : null;

const safe = (fn, fallback = null) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

const randomId = () =>
  safe(() => crypto.randomUUID()) ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

// Identifies this page load (a refresh gets a new one). index.html creates it
// first so its early heartbeat uses the same ID.
const PAGE_ID = window.__dmvnavPageId || randomId();
window.__dmvnavPageId = PAGE_ID;

// ---- per-tab storage -------------------------------------------------------

const getSessionId = () => safe(() => sessionStorage.getItem(SESSION_ID_KEY));

const setSessionId = (sid) => {
  safe(() => {
    if (sid) sessionStorage.setItem(SESSION_ID_KEY, sid);
    else sessionStorage.removeItem(SESSION_ID_KEY);
  });
};

export const isTabLoggedIn = () =>
  safe(() => sessionStorage.getItem(LOGGED_IN_KEY) === 'true' && !!getSessionId(), false);

const clearTab = () => {
  safe(() => {
    sessionStorage.removeItem(LOGGED_IN_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    sessionStorage.removeItem('email');
    sessionStorage.removeItem('first_name');
    sessionStorage.removeItem('last_name');
  });
};

export const touchActivity = () => {
  safe(() => sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())));
};

const lastActivity = () =>
  Number(safe(() => sessionStorage.getItem(LAST_ACTIVITY_KEY)) || 0);

// ---- attach the session ID to every API request -----------------------------

const isApiUrl = (url) => {
  const href = typeof url === 'string' ? url : url?.url || '';
  return SERVER_URL ? href.startsWith(SERVER_URL) : href.startsWith('/api') || href.startsWith('/dmvnavigator');
};

// The server returns the tab's session ID on every response: it changes when a
// new session is created (first request, or after the old one ended).
const captureSession = (getHeader) => {
  if (getHeader(SESSION_ENDED_HEADER)) {
    setSessionId(null);
  }
  const sid = getHeader(SESSION_HEADER);
  if (sid) setSessionId(sid);
};

let hooksInstalled = false;

const installRequestHooks = () => {
  if (hooksInstalled) return;
  hooksInstalled = true;

  axios.interceptors.request.use((config) => {
    const sid = getSessionId();
    if (sid && isApiUrl(config.url)) {
      config.headers = config.headers || {};
      config.headers[SESSION_HEADER] = sid;
    }
    return config;
  });
  const fromAxios = (response) => {
    if (response && isApiUrl(response.config?.url)) {
      captureSession((name) => response.headers?.[name.toLowerCase()]);
    }
  };
  axios.interceptors.response.use(
    (response) => {
      fromAxios(response);
      return response;
    },
    (error) => {
      fromAxios(error?.response);
      return Promise.reject(error);
    }
  );

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    if (!isApiUrl(input)) return originalFetch(input, init);
    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    const sid = getSessionId();
    if (sid) headers.set(SESSION_HEADER, sid);
    const response = await originalFetch(input, { ...init, headers });
    captureSession((name) => response.headers.get(name));
    return response;
  };
};

// ---- server calls -----------------------------------------------------------

// Form-encoded; the session ID goes in the header like any other call.
// Resolves to the HTTP status, or 0 when the server could not be reached.
const postForm = async (path, fields) => {
  try {
    const response = await fetch(`${SERVER_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(fields),
    });
    return response.status;
  } catch {
    return 0;
  }
};

// Only an explicit 401/403 means "this tab is logged out". A network blip
// (status 0) or a 5xx must not log anyone out.
const isDenied = (status) => status === 401 || status === 403;

const heartbeat = () => postForm('/api/session/tabs/heartbeat', { page_id: PAGE_ID });

// sendBeacon can't set headers, so the beacon routes take the ID as a field.
const beacon = (path, fields) => {
  const sid = getSessionId();
  if (!sid || !navigator.sendBeacon) return false;
  return navigator.sendBeacon(
    `${SERVER_URL}${path}`,
    new URLSearchParams({ ...fields, _tab_session: sid })
  );
};

// End this tab's server session. sendBeacon so it still goes through when it
// is called while the tab is closing; fetch with keepalive as the fallback.
export const endServerSession = () => {
  const sid = getSessionId();
  if (!sid) return;
  try {
    if (beacon('/dmvnavigator/logout', {})) return;
  } catch {
    // fall through
  }
  fetch(`${SERVER_URL}/dmvnavigator/logout`, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ _tab_session: sid }),
  }).catch(() => {});
};

const goToLogin = () => {
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
};

// ---- public API -------------------------------------------------------------

// The logged-in user's details for THIS tab. Per tab because two tabs can be
// logged in as different users. Falls back to localStorage for the pre-login
// flows (signup / forgot password) that stash the email there.
export const getProfile = (key) =>
  safe(() => sessionStorage.getItem(key)) ?? safe(() => localStorage.getItem(key));

export const setProfile = (email, firstName, lastName) => {
  safe(() => {
    sessionStorage.setItem('email', email ?? '');
    sessionStorage.setItem('first_name', firstName ?? '');
    sessionStorage.setItem('last_name', lastName ?? '');
  });
};

// Log THIS tab out: its server session and its storage. Other tabs keep their
// own sessions. The caller handles redux and navigation for this tab.
export const endSession = () => {
  endServerSession();
  clearTab();
};

// Called once a login (password or MFA) succeeds, before navigating. The login
// requests already created this tab's session; this marks the tab logged in
// and registers the page so tab close can be detected.
export const startTabSession = async () => {
  safe(() => sessionStorage.setItem(LOGGED_IN_KEY, 'true'));
  touchActivity();
  await heartbeat();
};

let listenersInstalled = false;

const installListeners = () => {
  if (listenersInstalled) return;
  listenersInstalled = true;

  // Answer a tab that is checking whether it is a duplicate of this one.
  if (channel) {
    channel.addEventListener('message', (event) => {
      const msg = event.data || {};
      if (msg.type === 'who-has-session' && msg.sid && msg.sid === getSessionId()) {
        channel.postMessage({ type: 'answer', requestId: msg.requestId });
      }
    });
  }

  // pagehide fires on refresh, tab close, browser close and navigating away -
  // the browser can't tell which. Tell the server; a refresh comes back within
  // the grace window, anything else doesn't.
  window.addEventListener('pagehide', () => {
    if (isTabLoggedIn()) beacon('/api/session/tabs/leaving', { page_id: PAGE_ID });
  });

  // Restored from the back/forward cache after pagehide: re-check.
  window.addEventListener('pageshow', async (event) => {
    if (event.persisted && isTabLoggedIn() && isDenied(await heartbeat())) {
      clearTab();
      goToLogin();
    }
  });
};

// Ask the other open tabs whether one of them uses this session ID.
const isDuplicate = (sid) => new Promise((resolve) => {
  if (!channel) {
    resolve(false);
    return;
  }
  const requestId = randomId();
  const onMessage = (event) => {
    const msg = event.data || {};
    if (msg.type === 'answer' && msg.requestId === requestId) {
      cleanup();
      resolve(true);
    }
  };
  const timer = setTimeout(() => {
    cleanup();
    resolve(false);
  }, DUPLICATE_CHECK_TIMEOUT_MS);
  const cleanup = () => {
    clearTimeout(timer);
    channel.removeEventListener('message', onMessage);
  };
  channel.addEventListener('message', onMessage);
  channel.postMessage({ type: 'who-has-session', sid, requestId });
});

// Runs before React renders, so pages see the right login state on first paint.
export const bootstrapTabSession = async () => {
  installRequestHooks();
  installListeners();

  const sid = getSessionId();
  if (!sid || !isTabLoggedIn()) {
    // A new tab, or a tab that was logged out: it must log in.
    safe(() => sessionStorage.removeItem(LOGGED_IN_KEY));
    return;
  }

  // A refresh, or a duplicated tab (which copies sessionStorage).
  if (Date.now() - lastActivity() > IDLE_LIMIT_MS) {
    endSession();
    return;
  }

  // index.html sends this heartbeat before the app bundle has loaded, to come
  // back well inside the server's grace window.
  const early = window.__dmvnavHeartbeat;
  const status = early ? await early : await heartbeat();
  if (isDenied(status)) {
    clearTab();
    return;
  }

  // A duplicate shares the original's session; give it its own copy, so the
  // two can be logged out (or closed) independently.
  if (await isDuplicate(sid)) {
    try {
      const response = await fetch(`${SERVER_URL}/api/session/tabs/fork`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ page_id: PAGE_ID }),
      });
      const body = response.ok ? await response.json() : null;
      if (body?.tab_session) {
        setSessionId(body.tab_session);
      } else {
        clearTab();
      }
    } catch {
      clearTab();
    }
  }
};

// Activity tracking for a logged-in page: records activity, heartbeats the
// server while the user is active, and calls onIdle after IDLE_LIMIT_MS with
// no activity in this tab. If the server says this tab's session is gone, the
// tab goes to the login page. Returns a cleanup function.
export const startActivityTracking = (onIdle) => {
  let lastWrite = 0;
  let lastHeartbeat = Date.now();

  const onActivity = () => {
    const now = Date.now();
    if (now - lastWrite > ACTIVITY_WRITE_THROTTLE_MS) {
      lastWrite = now;
      touchActivity();
    }
    if (now - lastHeartbeat > HEARTBEAT_INTERVAL_MS) {
      lastHeartbeat = now;
      heartbeat().then((status) => {
        if (isDenied(status) && isTabLoggedIn()) {
          clearTab();
          goToLogin();
        }
      });
    }
  };

  const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach((event) => window.addEventListener(event, onActivity, { passive: true }));
  touchActivity();

  const timer = setInterval(() => {
    if (isTabLoggedIn() && Date.now() - lastActivity() > IDLE_LIMIT_MS) {
      onIdle();
    }
  }, IDLE_CHECK_INTERVAL_MS);

  return () => {
    events.forEach((event) => window.removeEventListener(event, onActivity));
    clearInterval(timer);
  };
};
